import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Avatar,
  CircularProgress,
  Alert,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Grid,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  DateRange as DateRangeIcon,
  VerifiedUser as VerifiedUserIcon,
  Map as MapIcon,
} from "@mui/icons-material";
import { apiCall } from "../config/api";

const UserDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [userPins, setUserPins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [banDialogOpen, setBanDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [banReason, setBanReason] = useState("");

  useEffect(() => {
    fetchUserDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchUserDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiCall(`/api/users/${id}`, { method: "GET" });
      if (response.success) {
        setUser(response.user);
        setUserPins(response.pins || []);
      } else {
        setError(response.error || "Kullanıcı bulunamadı");
      }
    } catch (err) {
      console.error("Error fetching user:", err);
      setError("Kullanıcı detayları yüklenirken hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  const handleBanToggle = async () => {
    try {
      const response = await apiCall(`/api/users/${id}/ban`, {
        method: "POST",
        body: JSON.stringify({
          isBanned: !user.isBanned,
          reason: banReason,
        }),
      });
      if (response.success) {
        setUser({ ...user, isBanned: !user.isBanned });
        alert(
          user.isBanned ? "Kullanıcı ban'dan çıkarıldı" : "Kullanıcı ban'landı"
        );
        setBanDialogOpen(false);
        setBanReason("");
      }
    } catch (err) {
      alert("İşlem başarısız: " + err.message);
    }
  };

  const handleDeleteUser = async () => {
    try {
      const response = await apiCall(`/api/users/${id}`, {
        method: "DELETE",
      });
      if (response.success) {
        alert("Kullanıcı silindi");
        navigate("/user-management");
      }
    } catch (err) {
      alert("Silme başarısız: " + err.message);
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  const handleVerifyEmail = async () => {
    try {
      const response = await apiCall(`/api/users/${id}/verify-email`, {
        method: "POST",
      });
      if (response.success) {
        setUser({ ...user, emailVerified: true });
        alert("Email doğrulandı");
      }
    } catch (err) {
      alert("İşlem başarısız: " + err.message);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "80vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !user) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error || "Kullanıcı bulunamadı"}</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/user-management")}
          sx={{ mt: 2 }}
        >
          Geri Dön
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: "auto" }}>
      {/* Header */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/user-management")}
        >
          Geri
        </Button>
        <Typography variant="h4">Kullanıcı Detayı</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setDeleteDialogOpen(true)}
          >
            Sil
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* User Info Card */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Stack spacing={3} alignItems="center">
              {/* Profile Picture */}
              <Avatar
                src={user.photoURL}
                alt={user.displayName}
                sx={{ width: 120, height: 120, mb: 2 }}
              />

              <Typography variant="h5" textAlign="center">
                {user.displayName || "İsimsiz Kullanıcı"}
              </Typography>

              {/* Status Chips */}
              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                justifyContent="center"
              >
                {user.isAdmin && <Chip label="Admin" color="error" />}
                {user.emailVerified ? (
                  <Chip
                    label="Email Doğrulandı"
                    color="success"
                    icon={<VerifiedUserIcon />}
                  />
                ) : (
                  <Chip label="Email Doğrulanmamış" color="warning" />
                )}
                {user.isBanned && <Chip label="Ban'lı" color="error" />}
                {user.isAnonymous && <Chip label="Anonim" color="default" />}
              </Stack>

              <Divider sx={{ width: "100%" }} />

              {/* User Details */}
              <Box sx={{ width: "100%" }}>
                <Stack spacing={2}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <EmailIcon color="action" />
                    <Typography variant="body2">
                      <strong>Email:</strong> {user.email || "Belirtilmemiş"}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <PersonIcon color="action" />
                    <Typography variant="body2">
                      <strong>UID:</strong> {user.id}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <DateRangeIcon color="action" />
                    <Typography variant="body2">
                      <strong>Kayıt Tarihi:</strong>{" "}
                      {user.createdAt
                        ? new Date(user.createdAt).toLocaleDateString("tr-TR")
                        : "N/A"}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <DateRangeIcon color="action" />
                    <Typography variant="body2">
                      <strong>Son Giriş:</strong>{" "}
                      {user.lastSignInTime
                        ? new Date(user.lastSignInTime).toLocaleDateString(
                            "tr-TR"
                          )
                        : "N/A"}
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <Divider sx={{ width: "100%" }} />

              {/* Quarantine Info */}
              <Box sx={{ width: "100%" }}>
                <Typography variant="h6" gutterBottom>
                  Karantina Durumu
                </Typography>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Engellenme Sayısı
                    </Typography>
                    <Typography
                      variant="h4"
                      color={
                        user.blockedCount >= 7 ? "error.main" : "text.primary"
                      }
                    >
                      {user.blockedCount || 0}
                    </Typography>
                  </Box>
                  {user.isQuarantined && (
                    <Chip
                      label="⚠️ Karantinada"
                      color="warning"
                      sx={{ alignSelf: "flex-start" }}
                    />
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {user.blockedCount >= 7
                      ? "⚠️ Kullanıcı 7+ engellenme aldı. Pinleri otomatik gizlendi."
                      : `${
                          7 - (user.blockedCount || 0)
                        } engellenme daha karantinaya alınır.`}
                  </Typography>
                </Stack>
              </Box>

              <Divider sx={{ width: "100%" }} />

              {/* Admin Actions */}
              <Box sx={{ width: "100%" }}>
                <Typography variant="h6" gutterBottom>
                  Yönetim İşlemleri
                </Typography>
                <Stack spacing={1}>
                  <Button
                    variant="contained"
                    color={user.isBanned ? "success" : "error"}
                    startIcon={
                      user.isBanned ? <CheckCircleIcon /> : <BlockIcon />
                    }
                    onClick={() => setBanDialogOpen(true)}
                    fullWidth
                  >
                    {user.isBanned ? "Ban'ı Kaldır" : "Ban'la (Hard Ban)"}
                  </Button>
                  {!user.emailVerified && (
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<VerifiedUserIcon />}
                      onClick={handleVerifyEmail}
                      fullWidth
                    >
                      Email'i Doğrula
                    </Button>
                  )}
                </Stack>
              </Box>

              {/* Statistics */}
              <Box sx={{ width: "100%" }}>
                <Typography variant="h6" gutterBottom>
                  İstatistikler
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h4" color="primary">
                          {userPins.length}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Toplam Pin
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h4" color="success.main">
                          {userPins.filter((p) => p.status === "active").length}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Aktif Pin
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            </Stack>
          </Paper>
        </Grid>

        {/* User Pins List */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Kullanıcının Pinleri ({userPins.length})
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {userPins.length === 0 ? (
              <Alert severity="info">
                Bu kullanıcının henüz pin'i bulunmuyor.
              </Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Resim</TableCell>
                      <TableCell>Başlık</TableCell>
                      <TableCell>Tür</TableCell>
                      <TableCell>Durum</TableCell>
                      <TableCell>Tarih</TableCell>
                      <TableCell>İşlemler</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {userPins.map((pin) => (
                      <TableRow key={pin.id} hover>
                        <TableCell>
                          {pin.imageUrl ? (
                            <Avatar
                              src={pin.imageUrl}
                              variant="rounded"
                              sx={{ width: 40, height: 40 }}
                            />
                          ) : (
                            <Avatar
                              variant="rounded"
                              sx={{ width: 40, height: 40 }}
                            >
                              <MapIcon />
                            </Avatar>
                          )}
                        </TableCell>
                        <TableCell>
                          {pin.title || pin.name || "Başlıksız"}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={
                              pin.pinType === "audio" || pin.audioUrl
                                ? "Sesli"
                                : "Metin"
                            }
                            color={
                              pin.pinType === "audio" || pin.audioUrl
                                ? "primary"
                                : "default"
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={pin.status === "active" ? "Aktif" : "Pasif"}
                            color={
                              pin.status === "active" ? "success" : "default"
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {pin.createdAt
                            ? new Date(pin.createdAt).toLocaleDateString(
                                "tr-TR"
                              )
                            : "N/A"}
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            onClick={() =>
                              window.open(`/pins/${pin.id}`, "_blank")
                            }
                          >
                            Göster
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Ban Dialog */}
      <Dialog open={banDialogOpen} onClose={() => setBanDialogOpen(false)}>
        <DialogTitle>
          {user.isBanned ? "Ban'ı Kaldır" : "Kullanıcıyı Ban'la"}
        </DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            {user.isBanned
              ? "Bu kullanıcının ban'ını kaldırmak istediğinizden emin misiniz?"
              : "Bu kullanıcıyı ban'lamak istediğinizden emin misiniz?"}
          </Typography>
          {!user.isBanned && (
            <TextField
              fullWidth
              label="Ban Sebebi (Opsiyonel)"
              value={banReason}
              onChange={(e) => setBanReason(e.target.value)}
              multiline
              rows={3}
              sx={{ mt: 2 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBanDialogOpen(false)}>İptal</Button>
          <Button
            onClick={handleBanToggle}
            color={user.isBanned ? "success" : "error"}
            variant="contained"
          >
            {user.isBanned ? "Ban'ı Kaldır" : "Ban'la"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Kullanıcıyı Sil</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Bu kullanıcıyı silmek istediğinizden emin misiniz? Bu işlem geri
            alınamaz!
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Kullanıcının tüm pinleri de silinecektir.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>İptal</Button>
          <Button onClick={handleDeleteUser} color="error" variant="contained">
            Sil
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserDetailPage;
